import {Inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpParams, HttpResponse, HttpResponseBase} from '@angular/common/http';
import {BehaviorSubject, combineLatest, Observable, of, Subject, throwError} from 'rxjs';
import {catchError, filter, map, take, throttleTime} from 'rxjs/operators';

import {environment} from '@env/environment';
import {TokenService} from '../security/token.service';
import {AuthenticatedUserService} from '@app/core/service/authenticated-user.service';
import {NavigationExtras, Router} from '@angular/router';
import {DOCUMENT} from '@angular/common';
import {select, Store} from '@ngrx/store';
import {getAuthenticatedUser, getSignOutRedirectUrl, State} from '@app/app.reducers';
import {isValidURL} from '@app/core/utils/other-utils';
import {
    INVALID_PASSWORD_ERROR_MESSAGE,
    INVALID_PASSWORD_STATUS_CODE,
    RoleType,
    SCHOOL_PASSPORT_AUTH_COOKIE_NAME,
    SEND_SMS_FAILED_STATUS_CODE,
    User,
    UserStatus
} from '@app/core/model/user';
import {CustomerService} from '@app/core/service/customer.service';
import {CookieStorage} from '@app/core/security/cookie-storage';
import {BrowserStorage} from '@app/core/security/browser-storage';
import {AuthenticationSuccessAction, SignOutAction} from '@app/views/common/auth.actions';
import {SplashScreenService} from '@app/core/service/splash-screen.service';
import {OidcSecurityProxyService} from '@app/core/service/oidc.security.proxy.service';
import * as auth from '@app/views/common/auth.actions';

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService {

    public isCheckETXAccessTokenInProgress$ = new BehaviorSubject(false);
    private checkETXAccessToken_: Subject<null>;

    constructor(
        public tokenService: TokenService,
        public authenticatedUserService: AuthenticatedUserService,
        public router: Router,
        private http: HttpClient,
        private store: Store<State>,
        private splashScreen: SplashScreenService,
        private cookieStorage: CookieStorage,
        private oidcSecurityProxyService: OidcSecurityProxyService,
        @Inject(DOCUMENT) private document: Document
    ) {
        this.checkETXAccessToken_ = new Subject<null>();
        this.checkETXAccessToken_.pipe(
            throttleTime(5000),
            filter((events: any) =>
                events?.length !== 0
            )
        ).subscribe(() =>
            this.startUserReauthenticationIfNeeded()
        );
    }

    static tryDecodeURI(value: string | any): string | any {
        try {
            return decodeURI(value);
        } catch (err) {
            return value;
        }
    }

    static tryParseJSON(value: string | any): string | any {
        try {
            return JSON.parse(value);
        } catch (err) {
            return value;
        }
    }

    static decodeAndParseObjectValues(value: {}) {
        Object.keys(value).forEach((key) => {
            if ((typeof value[key] === 'string') || (value[key] instanceof String)) {
                value[key] = AuthenticationService.tryDecodeURI(value[key]);
            }
            value[key] = AuthenticationService.tryParseJSON(value[key]);
        });
        return value;
    }

    static getBrowserStorageList(key: string, browserStorage: BrowserStorage): {} {
        const browserStorageListJSON = browserStorage.read(key);
        return AuthenticationService.tryParseJSON(browserStorageListJSON) || {};
    }

    static getCookiesStorageList(key: string, cookieStorage: CookieStorage): {} {
        const cookiesStorageListDirty = cookieStorage.readAll();
        const cookiesStorageList = {};
        Object.keys(cookiesStorageListDirty).forEach((cookieKey) => {
            let newCookieKey: string;
            if (cookieKey.indexOf(key + '_') > -1) {
                newCookieKey = cookieKey.slice((key + '_').length);
                cookiesStorageList[newCookieKey] = cookiesStorageListDirty[cookieKey];

                if ((newCookieKey === 'authnResult')
                    && cookiesStorageList[newCookieKey]
                    && cookiesStorageList[newCookieKey]['id_token']
                ) {
                    cookiesStorageList[newCookieKey]['refresh_token'] = ' '; // Hardcode hack to use refresh_token from cookie user_sess
                }
            }
        });
        return cookiesStorageList;
    }

    static htmlDecode(input) {
        const doc = new DOMParser().parseFromString(input, 'text/html');
        return doc.documentElement.textContent;
    }

    login(username: string, password?: string, mfaCode?: string, resetCode?: string): Observable<HttpResponseBase> {
        const httpOptions = {
            observe: 'response' as const,
            responseType: 'text' as const
        };
        let params = new HttpParams()
            .append('username', username);

        if (mfaCode) {
            params = params.append('mfaCode', mfaCode);
        } else {
            if (resetCode) {
                params = params.append('resetCode', resetCode);
            } else {
                params = params.append('password', password);
            }
        }

        this.isCheckETXAccessTokenInProgress$.next(false);

        return this.http.post<any>(`${environment.datahubHost}/auth/login`, params, {observe: 'response'}).pipe(
            map((response: HttpResponse<any>) => response)
        );
    }

    logout(): Observable<string> {
        return this.http.get(`${environment.datahubHost}/auth/logout`, {responseType: 'text'});
    }

    logoutETX(): Observable<any> {
        return this.http.post(`${environment.etxBaseURI}/api/v2/webpassport/signout`, {});
    }

    verify() {
        return this.http.get(`${environment.datahubBaseURI}/api/token/verify`).toPromise();
    }

    getToken() {
        this.tokenService.getToken();
    }

    sendResetPasswordLink(email: string): Observable<any> {
        const url = `${environment.datahubBaseURI}/api/v2/useraccess/sendResetLinkForPassword?email=${email}`;
        return this.http.post(url, {});
    }

    changePasswordCheckSig(userGuid: string, ts: string, sig: string): Observable<any> {
        const url = `${environment.datahubBaseURI}/api/v2/useraccess/changePasswordCheckSig`;
        const params = {
            guid: userGuid,
            ts: ts,
            sig: encodeURIComponent(sig)
        };
        return this.http.post(url, params);
    }

    changePassword(password: string, userGuid: string, ts: string, sig: string): Observable<any> {
        const url = `${environment.datahubBaseURI}/api/v2/useraccess/changePassword`;
        const params = {
            password: password,
            guid: userGuid,
            ts: ts,
            sig: encodeURIComponent(sig)
        };
        return this.http.post(url, params)
            .pipe(
                catchError((error): Observable<any> => {
                    if (error.status === INVALID_PASSWORD_STATUS_CODE) {
                        error.message = INVALID_PASSWORD_ERROR_MESSAGE;
                    }
                    return throwError(error);
                })
            );
    }

    setupPasswordCheckSig(userGuid: string, ts: string, sig: string): Observable<any> {
        const url = `${environment.datahubBaseURI}/api/v2/useraccess/setUpPasswordCheckSig`;
        const params = {
            guid: userGuid,
            ts: ts,
            sig: encodeURIComponent(sig)
        };
        return this.http.post(url, params);
    }

    setupPassword(password: string, userGuid: string, ts: string, sig: string): Observable<any> {
        const url = `${environment.datahubBaseURI}/api/v2/useraccess/setUpPassword`;
        const params = {
            password: password,
            guid: userGuid,
            ts: ts,
            sig: encodeURIComponent(sig)
        };
        return this.http.post(url, params)
            .pipe(
                catchError((error): Observable<any> => {
                    if (error.status === INVALID_PASSWORD_STATUS_CODE) {
                        error.message = INVALID_PASSWORD_ERROR_MESSAGE;
                    }
                    return throwError(error);
                })
            );

    }

    checkServerStatus(): Observable<boolean> {
        return this.http.get<string>(`${environment.datahubBaseURI}/actuator/health`)
            .pipe(
                map((status: string) => true),
                catchError(() => of(false))
            );
    }

    // Redirect to login page or identity provider login page
    redirectToLoginPage(navExtras?: NavigationExtras, redirectUrl?: string) {
        if (redirectUrl && isValidURL(redirectUrl)) {
            this.redirectTo(navExtras, redirectUrl);
        } else {
            this.store.pipe(select(getSignOutRedirectUrl))
                .pipe(take(1))
                .subscribe((signOutRedirectUrl: string) => {
                    const userRole = this.authenticatedUserService.setPreviousUserRole();
                    const matchUserRole = environment.customer?.restrictedInternalLoginRoles?.includes(userRole);
                    if (!signOutRedirectUrl && matchUserRole) {
                        this.logout().subscribe(
                            (res) => this.redirectTo(navExtras, res),
                            (error) => {
                                if (!signOutRedirectUrl && isValidURL(error.error)) {
                                    signOutRedirectUrl = error.error;
                                }
                                this.redirectTo(navExtras, signOutRedirectUrl);
                            }
                        );
                    } else {
                        this.redirectTo(navExtras, signOutRedirectUrl);
                    }
                });
        }
    }

    // Redirect to login page or identity provider login page
    private redirectTo(navExtras: NavigationExtras, redirectUrl: string) {
        if (isValidURL(redirectUrl)) {
            this.document.location.href = redirectUrl;
        } else {
            let loginRoute = '/login';
            // if (environment.stsServer) {
            if (!CustomerService.checkIsAmplifyCustomer()) {
                const lastAuthenticatedRole = localStorage.getItem('lastAuthenticatedRole');
                loginRoute = (lastAuthenticatedRole === RoleType.INSTITUTIONAL_ADMIN)
                    ? '/login/district'
                    : (lastAuthenticatedRole === RoleType.APPLICATION_ADMIN || lastAuthenticatedRole === RoleType.SYS_ADMIN)
                        ? '/login/vendor'
                        : '/login';
            }
            this.router.navigate([loginRoute], navExtras);
        }
    }

    sendVerificationCodeAgain(): Observable<boolean> {
        return this.http.get<string>(`${environment.datahubBaseURI}/mfa/send/code`)
            .pipe(
                map(() => true),
                catchError((error: HttpErrorResponse) => {
                    if (error.status === SEND_SMS_FAILED_STATUS_CODE) {
                        return throwError(error);
                    } else {
                        return this.handleError(error);
                    }
                })
            );
    }

    checkETXAuth() {
        const etxAccessToken = this.cookieStorage.read(SCHOOL_PASSPORT_AUTH_COOKIE_NAME);
        if (etxAccessToken) {
            if (this.authenticatedUserService.isAuthenticated()) {
                this.oidcSecurityProxyService.getUserData()
                    .subscribe((userData: any) => {
                        if (userData === null) {
                            this.oidcSecurityProxyService.refreshSession()
                                .subscribe(() => {});
                        } else {
                            this.checkETXAccessToken();
                        }
                    });
            } else if (this.router.url !== '/auth/oauth') {
                this.getXAuthToken();
            }
        }
    }

    getXAuthToken(returnUrl?: string) {
        this.splashScreen.setHideOnNavigationEnd(false);

        const httpOptions = {
            headers: {
                'Access-Control-Allow-Credentials': 'true',
                'Access-Control-Allow-Origin': window.location.origin,
                'Access-Control-Allow-Methods': 'OPTIONS, GET, POST'
            },
            withCredentials: true,
            observe: 'response' as const,
            responseType: 'text' as const
        };

        combineLatest([
            this.oidcSecurityProxyService.getPayloadFromAccessToken(),
            this.http.get(`${environment.datahubHost}/auth/opaque/token`, httpOptions)
        ]).pipe(
            map(([accessTokenData, res]) => {
                if (res.headers.has('X-Auth-Token')) {
                    this.tokenService.removeToken();
                    this.tokenService.setToken(res.headers.get('X-Auth-Token'));

                    // this.store.dispatch(new IntegrationClearState());
                    this.store.dispatch(new AuthenticationSuccessAction({
                        etxOrgId: <number>accessTokenData?.org_id
                    }));
                } else {
                    // ToDo: redirect to login page
                    // this.router.navigate(['']);
                }
                const previousPagePath = (returnUrl || decodeURIComponent(sessionStorage.getItem('previousPage') || ''));

                this.store.pipe(
                    select(getAuthenticatedUser),
                    filter((user: User) => !!user),
                    take(1)
                ).subscribe(user => {
                    this.router.navigateByUrl(previousPagePath || '');
                    this.splashScreen.setHideOnNavigationEnd(true);
                });

                this.isCheckETXAccessTokenInProgress$.next(false);
            })
        ).subscribe((res: any) => {
            this.oidcSecurityProxyService.refreshSession()
                .subscribe(() => {});
        },
        (error) => {
            this.splashScreen.setHideOnNavigationEnd(true);
            this.isCheckETXAccessTokenInProgress$.next(false);

            this.store.dispatch(new auth.AuthenticationErrorAction({error: {message: error.error}}));
            if (error.error === <string>UserStatus.TO_BE_DELETED) {
                this.router.navigate(['/auth/account-expired']);
            } else {
                const domain = this.cookieStorage.getDomain(environment.stsServer);
                this.cookieStorage.remove(SCHOOL_PASSPORT_AUTH_COOKIE_NAME, '/', domain);
                this.router.navigate(['']);
            }
        });
    }

    checkETXAccessToken() {
        this.isCheckETXAccessTokenInProgress$.next(true);
        this.checkETXAccessToken_.next(null);
    }

    startUserReauthenticationIfNeeded() {
        const etxAccessToken = this.cookieStorage.read(SCHOOL_PASSPORT_AUTH_COOKIE_NAME);
        if (etxAccessToken && this.authenticatedUserService.isAuthenticated()) {
            combineLatest([
                this.oidcSecurityProxyService.getPayloadFromAccessToken(),
                this.oidcSecurityProxyService.getUserData()
            ]).subscribe(([payload, userData]: [any, any]) => {
                if (!!userData?.guid && !!payload?.guid
                    && (userData?.guid !== payload?.guid)
                ) {
                    this.store.dispatch(new SignOutAction({authenticateWithETX: true}));
                } else {
                    this.isCheckETXAccessTokenInProgress$.next(false);
                }
            });
        } else {
            this.isCheckETXAccessTokenInProgress$.next(false);
        }
    }

    handleError(error, operation?: string) {
        const errorMessage = operation || 'Something bad happened, please try again later.';
        if (error instanceof HttpErrorResponse) {
            console.log(`status: ${error.status}, ${error.statusText}`);
        } else {
            console.log(`${errorMessage}:`, error);
        }
        return throwError(errorMessage);
    }
}
